import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../hooks/use-auth";
import { usePageLoader } from "@/contexts/page-loader-context";
import { useAuthStore } from "../stores/auth-store";
import { useEffect, useState, useRef } from "react";
import { useBrands } from "@/modules/brand";

export default function PublicGuard() {
	const { isAuthenticated, isBrandAvailable, isNameAvailable, currentWorkspaceId, loading, updateProfile, setWorkspaceId } = useAuth();
	const { validateOtp } = useAuthStore();
	const { indexBrands } = useBrands();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const [validating, setValidating] = useState(false);
	const [autoSelectingWorkspace, setAutoSelectingWorkspace] = useState(false);
	const autoSelectAttemptedRef = useRef(false);
	const { showLoader, hideLoader } = usePageLoader();
	const redirect = searchParams.get("redirect");
	const redirectQuery = redirect ? `?redirect=${encodeURIComponent(redirect)}` : "";

	useEffect(() => {
		console.log("is authenticated from public guard", isAuthenticated);
		const session_id = searchParams.get("session_id");
		const code = searchParams.get("code");

		// Reset validating state if we no longer have validation params
		if (!session_id || !code) {
			// setValidating(false);
			return;
		}

		// Don't validate if we're already on the failure page
		if (location.pathname === "/auth/failure") {
			return;
		}

		// Wait for auth check to complete before validating
		if (loading) {
			return;
		}

		// Don't validate if user is already authenticated
		if (isAuthenticated) {
			return;
		}

		if (session_id && code && !validating) {
			const validate = async () => {
				setValidating(true);
				try {
					const success = await validateOtp({ session_id, code });
					if (!success) {
						navigate("/auth/failure", { state: { fromValidation: true }, replace: true });
					}
				} catch (error) {
					console.error("Validation failed:", error);
					navigate("/auth/failure", { state: { fromValidation: true }, replace: true });
				} finally {
					setValidating(false);
				}
			};
			validate();
		}
	}, [searchParams, validateOtp, navigate, validating, isAuthenticated, loading, location.pathname]);

	// Auto-select single workspace when currentWorkspaceId is blank
	useEffect(() => {
		if (loading || !isAuthenticated || !isNameAvailable || !isBrandAvailable || currentWorkspaceId) return;
		if (autoSelectAttemptedRef.current || autoSelectingWorkspace) return;

		autoSelectAttemptedRef.current = true;
		setAutoSelectingWorkspace(true);

		const autoSelectWorkspace = async () => {
			const brands = await indexBrands();

			if (brands && brands.length === 1) {
				const brandId = brands[0].id;
				await updateProfile({ current_workspace_id: brandId });
				setWorkspaceId(brandId);
				navigate(`/${brandId}/dashboard`, { replace: true });
			} else {
				navigate("/brands", { replace: true });
			}
			setAutoSelectingWorkspace(false);
		};

		autoSelectWorkspace();
	}, [loading, isAuthenticated, isNameAvailable, isBrandAvailable, currentWorkspaceId, indexBrands, updateProfile, setWorkspaceId, navigate, autoSelectingWorkspace]);

	// Handle loader visibility
	useEffect(() => {
		// Don't show loader on failure page
		if (location.pathname === "/auth/failure") {
			hideLoader();
			return;
		}

		if (loading || validating || isAuthenticated || autoSelectingWorkspace) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [loading, validating, isAuthenticated, autoSelectingWorkspace, location.pathname, showLoader, hideLoader]);

	// Don't show loader on failure page - let it render immediately
	if (location.pathname === "/auth/failure") {
		return <Outlet />;
	}

	if (loading || validating || autoSelectingWorkspace) {
		return null;
	}

	if (isAuthenticated) {
		if (!isNameAvailable) {
			navigate(`/onboarding/complete-profile${redirectQuery}`, { replace: true });
		} else if (!isBrandAvailable) {
			navigate(`/onboarding/create-brand${redirectQuery}`, { replace: true });
		} else if (currentWorkspaceId) {
			if (redirect) {
				navigate(redirect, { replace: true });
			} else {
				navigate(`/${currentWorkspaceId}/dashboard`, { replace: true });
			}
		}
		// If currentWorkspaceId is null, the useEffect above will handle auto-selection
		return null;
	}

	return <Outlet />;
}
